/**
 * Editable Classification section for the Visit edit page.
 * Lets a user correct the assist/deny decision and the client classifications
 * recorded for a visit after the fact (mirrors the wizard's Classification step).
 */

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { Loader2, Save } from 'lucide-react'
import {
  GENERAL_CLASSIFICATIONS,
  PWD_CLASSIFICATIONS,
  OTHER_CLASSIFICATIONS,
  type ClientClassification,
} from '@/types/visit-wizard'
import {
  mapClassificationsToTypeIds,
  mapTypeIdsToClassifications,
} from '@/constants/classification-types'
import { useCreateVisitClassification } from '@/hooks/useVisits'
import { toast } from '@/lib/toast'
import type { VisitClassification } from '@/types/visit'

interface EditVisitClassificationProps {
  visitId: string
  classification?: VisitClassification
}

export function EditVisitClassification({ visitId, classification }: EditVisitClassificationProps) {
  const initialTypeIds: number[] = classification?.detail?.types || []
  const initialClassifications = mapTypeIdsToClassifications(initialTypeIds) as ClientClassification[]
  const initialDecision = classification?.classification_id === 101 ? 'deny' : 'assist'

  const [decision, setDecision] = useState<'assist' | 'deny'>(initialDecision)
  const [selected, setSelected] = useState<ClientClassification[]>(initialClassifications)

  const saveClassification = useCreateVisitClassification()

  const toggleClassification = (value: ClientClassification) => {
    setSelected((current) =>
      current.includes(value) ? current.filter((c) => c !== value) : [...current, value]
    )
  }

  const handleSave = async () => {
    try {
      await saveClassification.mutateAsync({
        visit_id: visitId,
        classification_id: decision === 'assist' ? 100 : 101,
        detail: { types: mapClassificationsToTypeIds(selected) },
      })
      toast.success('Classification updated successfully!')
    } catch (error: any) {
      toast.error('Failed to update classification', error.response?.data?.detail || error.message)
    }
  }

  const renderGroup = (label: string, options: ClientClassification[]) => (
    <div className="space-y-2">
      <p className="text-sm font-medium text-gray-700">{label}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {options.map((option) => (
          <div key={option} className="flex items-center gap-2">
            <Checkbox
              id={`classification-${option}`}
              checked={selected.includes(option)}
              onCheckedChange={() => toggleClassification(option)}
            />
            <Label htmlFor={`classification-${option}`} className="font-normal cursor-pointer">
              {option}
            </Label>
          </div>
        ))}
      </div>
    </div>
  )

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-700">Decision</p>
        <RadioGroup value={decision} onValueChange={(value) => setDecision(value as 'assist' | 'deny')}>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <RadioGroupItem value="assist" id="decision-assist" />
              <Label htmlFor="decision-assist" className="font-normal cursor-pointer">Assisted</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="deny" id="decision-deny" />
              <Label htmlFor="decision-deny" className="font-normal cursor-pointer">Denied</Label>
            </div>
          </div>
        </RadioGroup>
      </div>

      {renderGroup('General', GENERAL_CLASSIFICATIONS)}
      {renderGroup('PWD', PWD_CLASSIFICATIONS)}
      {renderGroup('Other', OTHER_CLASSIFICATIONS)}

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={handleSave}
          disabled={saveClassification.isPending}
          className="bg-primary hover:bg-primary/90"
        >
          {saveClassification.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Save Classification
        </Button>
      </div>
    </div>
  )
}
